import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { MyAccountDialog } from "@/components/MyAccountDialog";
import { UserPlus, KeyRound, Pencil, Dices, Users as UsersIcon, Trash2, AlertTriangle } from "lucide-react";

const roleBadge = (r) =>
  ({
    owner: { bg: "#F9F3E5", fg: "#B8860B", label: "Owner" },
    supervisor: { bg: "#DCFCE7", fg: "#15803D", label: "Supervisor" },
    management: { bg: "#DBEAFE", fg: "#1D4ED8", label: "Management" },
  }[r] || { bg: "#F3F1EC", fg: "#8A94A6", label: r });

function genPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 10; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

const EMPTY = { name: "", email: "", password: "", role: "supervisor", title: "", emp_id: "", shift: "", properties: [] };

function PropertyPills({ properties, selected, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {properties.map((p) => {
        const on = selected.includes(p.id);
        return (
          <button
            key={p.id}
            type="button"
            data-testid={`${testidPrefix}-${p.id}`}
            onClick={() => onToggle(p.id)}
            className={`sop-pill px-3 py-2 rounded-md border text-sm font-medium ${
              on
                ? "bg-[var(--gold-light)] border-[var(--gold)] text-[var(--gold)]"
                : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--gold)]/50"
            }`}
          >
            {p.name.replace(" Hotel", "")}
          </button>
        );
      })}
    </div>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([api.get("/admin/users"), api.get("/config/properties")])
      .then(([u, p]) => {
        setUsers(u.data);
        setProperties(p.data.properties);
        setShifts(p.data.shifts || []);
      })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  };
  useEffect(load, []);

  const stats = useMemo(
    () => ({
      total: users.length,
      supervisors: users.filter((u) => u.role === "supervisor").length,
      management: users.filter((u) => u.role === "management").length,
    }),
    [users]
  );

  const propShort = (pid) => (properties.find((p) => p.id === pid)?.name || pid).replace(" Hotel", "").replace(" Tamanna", "");

  const toggleIn = (setter) => (pid) =>
    setter((f) => ({ ...f, properties: f.properties.includes(pid) ? f.properties.filter((x) => x !== pid) : [...f.properties, pid] }));

  const onCreate = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6 || form.properties.length === 0) {
      toast.error("Fill name, email, password (6+ chars) and at least one property");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", { ...form, email: form.email.trim().toLowerCase(), shift: form.shift || null });
      toast.success(`Account created for ${form.email}`);
      setCreateOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      name: u.name,
      email: u.email,
      password: "",
      role: u.role,
      title: u.title || "",
      emp_id: u.emp_id || "",
      shift: u.shift || "",
      properties: u.properties || [],
    });
  };

  const onEdit = async () => {
    if (editForm.properties.length === 0) {
      toast.error("Select at least one property");
      return;
    }
    if (!editForm.email.trim() || !editForm.email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${editTarget.id}`, {
        name: editForm.name,
        email: editForm.email.trim().toLowerCase(),
        title: editForm.title,
        emp_id: editForm.emp_id,
        shift: editForm.shift || null,
        role: editForm.role,
        properties: editForm.properties,
      });
      toast.success("User updated");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    if (resetPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { password: resetPw });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null);
      setResetPw("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`User ${deleteTarget.email} deleted permanently`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onToggleAllYes = async (u, val) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { allow_all_yes: val });
      toast.success(`"All Yes" button ${val ? "enabled" : "disabled"} for ${u.name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const Kpi = ({ label, value, testid }) => (
    <div data-testid={testid} className="bg-white border border-[var(--border-warm)] rounded-lg p-5">
      <div className="hotel-overline">{label}</div>
      <div className="font-display text-3xl text-[var(--navy)] mt-1 font-mono-hotel">{value}</div>
    </div>
  );

  return (
    <Layout title="User Management" subtitle="Owner · Access Control">
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Kpi testid="kpi-total-users" label="Total Users" value={stats.total} />
        <Kpi testid="kpi-supervisors" label="Supervisors" value={stats.supervisors} />
      </div>

      <div className="bg-white border border-[var(--border-warm)] rounded-lg overflow-hidden">
        <div className="p-5 border-b border-[var(--border-subtle)] bg-[var(--surface-elev)] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[var(--navy)] text-[var(--gold)] flex items-center justify-center">
              <UsersIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="hotel-overline">Accounts</div>
              <h3 className="font-display text-2xl text-[var(--navy)]">All Users</h3>
            </div>
          </div>
          <Button
            data-testid="admin-create-user-button"
            onClick={() => setCreateOpen(true)}
            className="bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Create User
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow className="bg-[var(--surface-elev)]/60 hover:bg-[var(--surface-elev)]/60">
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Properties</TableHead>
                <TableHead>Emp ID</TableHead>
                <TableHead>All Yes</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const rb = roleBadge(u.role);
                const isSelf = me?.id === u.id;
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <div className="font-semibold text-[var(--navy)]">{u.name} {isSelf && <span className="text-xs text-[var(--gold)]">(You)</span>}</div>
                      <div className="text-xs text-[var(--muted)] font-mono-hotel">{u.email}</div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: rb.bg, color: rb.fg }}>{rb.label}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(u.properties || []).map((pid) => (
                          <span key={pid} className="px-2 py-0.5 rounded bg-[var(--surface-elev)] border border-[var(--border-subtle)] text-xs text-[var(--ink-2)]">{propShort(pid)}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono-hotel text-xs text-[var(--ink-2)]">{u.emp_id || "—"}</TableCell>
                    <TableCell>
                      {u.role === "supervisor" ? (
                        <Switch
                          data-testid={`all-yes-toggle-${u.id}`}
                          checked={u.allow_all_yes !== false}
                          onCheckedChange={(v) => onToggleAllYes(u, v)}
                        />
                      ) : (
                        <span className="text-xs text-[var(--muted)]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono-hotel text-xs text-[var(--ink-2)]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-right">
                      {(isSelf || u.role !== "owner") && (
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" data-testid={`edit-user-btn-${u.id}`} onClick={() => (isSelf ? setAccountOpen(true) : openEdit(u))} className="border-[var(--border-warm)]">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" data-testid={`reset-password-btn-${u.id}`} onClick={() => { if (isSelf) { setAccountOpen(true); } else { setResetTarget(u); setResetPw(""); } }} className="border-[var(--border-warm)]">
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          {!isSelf && (
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`delete-user-btn-${u.id}`}
                              onClick={() => setDeleteTarget(u)}
                              className="border-[var(--danger)]/40 text-[var(--danger)] hover:bg-[var(--danger-bg)]"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[var(--navy)]">Create User</DialogTitle>
            <DialogDescription>Issue credentials for a supervisor or manager. They log in from the home page with their email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Full name</Label>
                <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-white border-[var(--border-warm)]" placeholder="e.g. Suresh Patil" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Email</Label>
                <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-white border-[var(--border-warm)]" placeholder="name@tamanna.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select" className="mt-1.5 bg-white border-[var(--border-warm)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supervisor" data-testid="role-option-supervisor">Supervisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Default shift</Label>
                <Select value={form.shift || "none"} onValueChange={(v) => setForm({ ...form, shift: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="user-shift-select" className="mt-1.5 bg-white border-[var(--border-warm)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {shifts.map((s) => <SelectItem key={s} value={s}>{s.split("(")[0].trim()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Properties</Label>
              <div className="mt-1.5"><PropertyPills properties={properties} selected={form.properties} onToggle={toggleIn(setForm)} testidPrefix="create-property-toggle" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Title (optional)</Label>
                <Input data-testid="user-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-white border-[var(--border-warm)]" placeholder="Housekeeping Supervisor" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Emp ID (optional)</Label>
                <Input data-testid="user-empid-input" value={form.emp_id} onChange={(e) => setForm({ ...form, emp_id: e.target.value })} className="mt-1.5 bg-white border-[var(--border-warm)]" placeholder="TMN-SUP-XXX" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Password</Label>
              <div className="flex gap-2 mt-1.5">
                <Input data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white border-[var(--border-warm)] font-mono-hotel" placeholder="Min 6 characters" />
                <Button type="button" variant="outline" data-testid="generate-password-button" onClick={() => setForm({ ...form, password: genPassword() })} className="border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold-light)]">
                  <Dices className="w-4 h-4 mr-1" /> Generate
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-[var(--border-warm)]">Cancel</Button>
            <Button data-testid="create-user-submit-button" onClick={onCreate} disabled={busy} className="bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white">
              {busy ? "Creating…" : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[var(--navy)]">Edit User</DialogTitle>
            <DialogDescription>Update account details. Changing the email changes the login ID for this user.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Full name</Label>
                <Input data-testid="edit-name-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5 bg-white border-[var(--border-warm)]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Email (login ID)</Label>
                <Input data-testid="edit-email-input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1.5 bg-white border-[var(--border-warm)] font-mono-hotel" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-role-select" className="mt-1.5 bg-white border-[var(--border-warm)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Title</Label>
                <Input data-testid="edit-title-input" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="mt-1.5 bg-white border-[var(--border-warm)]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Emp ID</Label>
                <Input data-testid="edit-empid-input" value={editForm.emp_id} onChange={(e) => setEditForm({ ...editForm, emp_id: e.target.value })} className="mt-1.5 bg-white border-[var(--border-warm)]" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Default shift</Label>
              <Select value={editForm.shift || "none"} onValueChange={(v) => setEditForm({ ...editForm, shift: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="edit-shift-select" className="mt-1.5 bg-white border-[var(--border-warm)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {shifts.map((s) => <SelectItem key={s} value={s}>{s.split("(")[0].trim()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Properties</Label>
              <div className="mt-1.5"><PropertyPills properties={properties} selected={editForm.properties} onToggle={toggleIn(setEditForm)} testidPrefix="edit-property-toggle" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} className="border-[var(--border-warm)]">Cancel</Button>
            <Button data-testid="edit-user-submit-button" onClick={onEdit} disabled={busy} className="bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white">
              {busy ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[var(--navy)]">Reset Password</DialogTitle>
            <DialogDescription>Set a new password for <span className="font-mono-hotel">{resetTarget?.email}</span></DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">New password</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="reset-password-input" value={resetPw} onChange={(e) => setResetPw(e.target.value)} className="bg-white border-[var(--border-warm)] font-mono-hotel" placeholder="Min 6 characters" />
              <Button type="button" variant="outline" data-testid="reset-generate-button" onClick={() => setResetPw(genPassword())} className="border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold-light)]">
                <Dices className="w-4 h-4 mr-1" /> Generate
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} className="border-[var(--border-warm)]">Cancel</Button>
            <Button data-testid="reset-password-submit-button" onClick={onReset} disabled={busy} className="bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white">
              {busy ? "Resetting…" : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-user-dialog" className="max-w-md">
          <DialogHeader>
            <div className="w-14 h-14 rounded-full bg-[var(--danger-bg)] flex items-center justify-center mx-auto mb-2">
              <AlertTriangle className="w-7 h-7 text-[var(--danger)]" />
            </div>
            <DialogTitle className="font-display text-2xl text-center text-[var(--navy)]">Delete User?</DialogTitle>
            <DialogDescription className="text-center">
              <span className="font-mono-hotel">{deleteTarget?.email}</span> will be permanently deleted and will immediately lose access. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-[var(--border-warm)]">Cancel</Button>
            <Button data-testid="delete-confirm-button" onClick={onDelete} disabled={busy} className="bg-[var(--danger)] hover:bg-[#B91C1C] text-white">
              {busy ? "Deleting…" : (<><Trash2 className="w-4 h-4 mr-1" /> Delete Permanently</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MyAccountDialog open={accountOpen} onOpenChange={setAccountOpen} onSaved={load} />
    </Layout>
  );
}
